import React, { useEffect, useState, useContext } from "react"
import { db, auth } from "../../utils/firebase"
import firebase from "firebase"
import { NavLink } from "react-router-dom"
import moment from "moment"
import "./enquiry.scss"
import { ModalContext } from "../../utils/providers/modal"
import { CheckIcon, CopyIcon, LoadingIcon, TrashIcon } from "../../utils/svgs"

/**
 * UI components
 */
import Button from "../ui/button/button"
import Checkbox from "../ui/checkbox/checkbox"

const formatFollowUpDate = (followUp) => moment(followUp?.seconds || followUp?._seconds, "X").format("MMM D[,] YYYY")

/**
 * Functional component to return the enquiry table row.
 *
 * Memoised so toggling the selection on one row in a long list doesn't re-render
 * every other row - they only re-render when their own props actually change.
 * This relies on the parent passing stable callback references (useCallback).
 */
function Enquiry(props) {
	const [client, setClient] = useState(props.clientData || {})
	const [isRemoved, setIsRemoved] = useState(false)
	const [isAssigningSame, setIsAssigningSame] = useState(false)
	const [isDeleting, setIsDeleting] = useState(false)
	const [copiedContact, setCopiedContact] = useState("")

	const [followUp, setFollowUp] = useState(formatFollowUpDate(props.details?.follow_up))
	const { showModal } = useContext(ModalContext)

	/**
	 * Deconstruct the details from the props
	 */
	const { id, details, tab } = props

	// Store the client details in variables
	let clientEmail = client.email || details.email || null
	let clientPhone = client.phone || details.phone || null

	// If we have the requirement to mask particular client details
	if (props.maskDetails) {
		// Mask the entire email address
		clientEmail = clientEmail?.replace(/./g, "*") || null

		// Do the same for the phone number
		clientPhone = clientPhone?.replace(/./g, "*") || null
	}

	const copyContact = async (value, type) => {
		if (!value || props.maskDetails) return

		try {
			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(value)
			} else {
				const textarea = document.createElement("textarea")
				textarea.value = value
				textarea.setAttribute("readonly", "")
				textarea.style.position = "absolute"
				textarea.style.left = "-9999px"
				document.body.appendChild(textarea)
				textarea.select()
				document.execCommand("copy")
				document.body.removeChild(textarea)
			}
			setCopiedContact(type)
			setTimeout(() => setCopiedContact(""), 1200)
		} catch (err) {
			console.error("Unable to copy contact value", err)
		}
	}

	const ContactValue = ({ value, type, emptyLabel }) => {
		if (!value) return <small className="no-value">{emptyLabel}</small>
		if (props.maskDetails) return <>{value}</>

		const isCopied = copiedContact === type
		return (
			<button
				type="button"
				className={["enquiry-copy-contact", isCopied && "is-copied"].filter(Boolean).join(" ")}
				aria-label={`Copy ${type}`}
				onClick={() => copyContact(value, type)}>
				<span>{value}</span>
				<span className="enquiry-copy-contact-icon">
					{isCopied ? <CheckIcon /> : <CopyIcon />}
				</span>
			</button>
		)
	}

	/**
	 * Generate a string to represent the created date from the timestamps
	 */
	const dateFrom = moment(details.date_from?._seconds || details.date_from?.seconds, "X").format("MMM D[,] YYYY")

	/**
	 * Generate a string to represent when the enquiry was created (with seconds)
	 */
	const enquiredAt =
		details.created?.seconds || details.created?._seconds
			? moment(details.created?._seconds || details.created?.seconds, "X").format("MMM D[,] YYYY HH:mm:ss")
			: null

	/**
	 * Calculate the age of the enquiry in days
	 */
	const enquiryAge = details.created ? moment().diff(moment(details.created?._seconds || details.created?.seconds, "X"), "days") : 0

	/**
	 * On component load
	 */
	useEffect(() => {
		if (props.clientData) {
			setClient(props.clientData)
			return
		}

		if (!details.client) return

		/**
		 * Get the client record from the database
		 */
		db.doc(`clients/${details.client}`)
			.get()
			.then((clientDoc) => {
				/**
				 * Set the client data into the local state
				 */
				setClient(clientDoc.data())
			})
	}, [props.clientData, details.client])

	/**
	 * On component load
	 */
	useEffect(() => {
		if (tab === "REQUIRE_ATTENTION") {
			return () => null
		}
		/**
		 * Setup a listener on the enquiry document
		 */
		const unsubscribe = db.doc(`enquiries/${id}`).onSnapshot((enquirySnap) => {
			/**
			 * Was the change a removal of the enquiry?
			 */
			if (!enquirySnap.exists) {
				/**
				 * Push the exists state into the local state
				 */
				setIsRemoved(!enquirySnap.exists)
			} else {
				/**
				 * Pull the details from the enquriy document
				 */
				const { follow_up } = enquirySnap.data()
				/**
				 * Parse the timestamp as a date time string
				 */
				const followUpDate = formatFollowUpDate(follow_up)
				/**
				 * Then set this follow up date into the state
				 */
				setFollowUp(followUpDate)
			}
		})
		/**
		 * Remove the listener when the component unloads
		 */
		return () => unsubscribe()
	}, [tab, id])

	/**
	 * If the enquiry document is marked as removed
	 */
	if (isRemoved) {
		/**
		 * Just return an empty fragment
		 */
		return <></>
	} else {
		const priorAgentName = props.priorAgentUser
			? `${props.priorAgentUser.first_name || ""} ${props.priorAgentUser.last_name || ""}`.trim()
			: null

		const shouldPromptReassign = tab === "REQUIRE_ATTENTION" && props.priorAgentId && props.priorAgentUser

		const assignToAgent = async (agentId) => {
			setIsAssigningSame(true)
			await db.doc(`enquiries/${id}`).set(
				{
					agent: agentId,
					status: "BEING_PROCESSED",
				},
				{ merge: true }
			)
			await db.collection(`LOGS_enquiries`).add({
				type: "INITIAL_ASSIGNMENT",
				created: firebase.firestore.FieldValue.serverTimestamp(),
				enquiry: id,
				byUser: auth.currentUser.uid,
				fromUser: null,
				toUser: agentId,
			})
			setIsAssigningSame(false)
		}

		/**
		 * Permanently delete the enquiry (super admins only). The
		 * enquiryDocumentDeleted trigger handles detaching it from the client and
		 * archiving it into enquiries_removed. The document listener (or the manual
		 * flag for tabs without one) then unmounts this row.
		 */
		const deleteEnquiry = async () => {
			setIsDeleting(true)
			try {
				// The enquiryDocumentDeleted trigger detaches the enquiry from its
				// client and archives it into enquiries_removed, so we only delete
				// the doc here.
				await db.doc(`enquiries/${id}`).delete()
				setIsRemoved(true)
			} catch (err) {
				console.error("Unable to delete enquiry", err)
				setIsDeleting(false)
			}
		}

		const confirmDeleteEnquiry = () => {
			showModal({
				type: "ALERT",
				title: "Delete this enquiry?",
				body: "Once deleted, you won't be able to recover the data for this enquiry.",
				cancel: { label: "Cancel", action: () => null },
				next: { label: "Yes, delete", action: () => deleteEnquiry() },
			})
		}

		const handleAssignClick = () => {
			if (!shouldPromptReassign) {
				props.showAssignWindow(id)
				return
			}

			showModal({
				type: "ALERT",
				title: "Reassign Same User?",
				body: "This client has a previous enquiry with an assigned agent.",
				cancel: {
					label: "Choose another",
					action: () => props.showAssignWindow(id),
				},
				next: {
					label: "Yes",
					action: () => assignToAgent(props.priorAgentId),
				},
			})
		}

		/**
		 * Return the enquiry row
		 */
		return (
			<tr data-system-id={id}>
				{props.selectable && (
					<td className="enquiry-select-cell">
						<Checkbox
							className="enquiry-select-checkbox"
							checked={!!props.isSelected}
							onClick={() => props.onToggleSelect?.(id)}
						/>
					</td>
				)}

				<td>{details.site}</td>

				{tab === "REQUIRE_ATTENTION" && (
					<td>
						{enquiredAt && <>{enquiredAt}</>}
						{!enquiredAt && <small className="no-value">No date set</small>}
					</td>
				)}

				<td>
					{!props.maskDetails && (
						<NavLink to={`client/${details.client}`}>
							{client.first_name && `${client.first_name} ${client.last_name}`}
							{!client.first_name && <small className="no-value">No name set</small>}
						</NavLink>
					)}

					{props.maskDetails && (
						<>
							{client.first_name && `${client.first_name} ${client.last_name}`}
							{!client.first_name && <small className="no-value">No name set</small>}
						</>
					)}
				</td>

				{tab !== "REQUIRE_ATTENTION" && (
					<>
						<td>
							<ContactValue
								value={clientEmail}
								type="email"
								emptyLabel="No email set"
							/>
						</td>
						<td>
							<ContactValue
								value={clientPhone}
								type="telephone"
								emptyLabel="No number set"
							/>
						</td>
						<td>
							{followUp === "Invalid date" && <small>No date set</small>}

							{followUp !== "Invalid date" && <>{followUp}</>}
						</td>
					</>
				)}
				<td>{details.location}</td>
				<td>{dateFrom}</td>
				<td>{details.group_size}</td>
				{tab === "REQUIRE_ATTENTION" && (
					<td>
						{props.priorLoading && (
							<small className="no-value prior-enquiry-loading">
								<span className="prior-enquiry-loading-icon">
									<LoadingIcon />
								</span>
								Checking...
							</small>
						)}
						{!props.priorLoading && props.priorAgentId && priorAgentName && props.priorAgentOnLeaderboard && <>{priorAgentName}</>}
						{!props.priorLoading && props.priorAgentId && priorAgentName && !props.priorAgentOnLeaderboard && (
							<small className="no-value">Previous: {priorAgentName}</small>
						)}
						{!props.priorLoading && props.priorAgentId && !priorAgentName && (
							<small className="no-value">Previous: Unknown agent</small>
						)}
						{!props.priorLoading && !props.priorAgentId && props.hasPriorEnquiry && (
							<small className="no-value">No agent set</small>
						)}
						{!props.priorLoading && !props.priorAgentId && !props.hasPriorEnquiry && (
							<small className="no-value">No prior enquiry</small>
						)}
					</td>
				)}
				{tab !== "REQUIRE_ATTENTION" && <td>£{details.quoted ? details.quoted : 0}</td>}
				{tab !== "REQUIRE_ATTENTION" && (
					<td>
						{enquiryAge} {enquiryAge === 1 ? "day" : "days"}
					</td>
				)}
				<td
					className="is-button"
					style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", width: "fit-content" }}>
						<Button
							small={true}
							label="View"
							onClick={() => props.showEnquiryWindow(id)}
						/>

						<Button
							small={true}
							label="View in new window"
							onClick={() => window.open(`/enquiry/${id}`, "_blank")}
						/>

						{props.isSuperAdmin && (
							<Button
								small={true}
								className="enquiry-delete-button"
								icon={<TrashIcon />}
								loading={isDeleting}
								onClick={confirmDeleteEnquiry}
							/>
						)}

						{tab === "REQUIRE_ATTENTION" && (
							<Button
								small={true}
								label="Assign"
								loading={isAssigningSame}
								loadingText="Assigning..."
								onClick={handleAssignClick}
							/>
						)}
					</div>
				</td>
			</tr>
		)
	}
}

export default React.memo(Enquiry)
